import { Component, ErrorInfo, ReactNode } from "react";
import { captureRenderError } from "../../utils/sentry";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  retryCount: number;
}

export class GlobalErrorBoundary extends Component<Props, State> {
  private maxRetries = 3;

  public state: State = {
    hasError: false,
    retryCount: 0,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Global Error Boundary caught an error:", error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    // Capture the error in Sentry with React-specific context
    captureRenderError(error, {
      componentStack: errorInfo.componentStack || undefined,
    });

    // Store error data for debugging (keeping existing functionality)
    if (typeof window !== "undefined") {
      try {
        const errorData = {
          message: error.message,
          stack: error.stack,
          timestamp: new Date().toISOString(),
          url: window.location.href,
          userAgent: navigator.userAgent,
          componentStack: errorInfo.componentStack,
        };

        // Store in sessionStorage for potential debugging
        const existingErrors = JSON.parse(
          sessionStorage.getItem("app_errors") || "[]"
        );
        existingErrors.push(errorData);

        // Keep only the last 10 errors to prevent storage bloat
        if (existingErrors.length > 10) {
          existingErrors.splice(0, existingErrors.length - 10);
        }

        sessionStorage.setItem("app_errors", JSON.stringify(existingErrors));
      } catch (storageError) {
        console.warn("Failed to store error data:", storageError);
      }
    }
  }

  private handleRetry = () => {
    if (this.state.retryCount < this.maxRetries) {
      // Check if this is an authentication-related error
      const isAuthError = this.state.error?.message?.toLowerCase().includes('auth') ||
                         this.state.error?.message?.toLowerCase().includes('unauthorized') ||
                         this.state.error?.message?.toLowerCase().includes('token') ||
                         this.state.error?.message?.toLowerCase().includes('401') ||
                         this.state.error?.message?.toLowerCase().includes('403');
      
      if (isAuthError) {
        // For auth errors, clear Auth0 state and reload to get a fresh session
        try {
          // Clear Auth0-related storage
          const auth0Keys = Object.keys(localStorage).filter(key => 
            key.startsWith('@@auth0spajs@@') || 
            key.startsWith('auth0') ||
            key.startsWith('@@auth0')
          );
          auth0Keys.forEach(key => localStorage.removeItem(key));
          
          // Clear session storage auth items
          sessionStorage.removeItem('auth0_return_to');
          sessionStorage.removeItem('auth0_redirecting');
        } catch (e) {
          console.warn('Failed to clear auth storage:', e);
        }
        
        // Reload the page to get a fresh Auth0 session
        window.location.reload();
        return;
      }
      
      // For non-auth errors, set a flag to prevent automatic Auth0 redirect
      // This gives the app a chance to recover without triggering a login redirect
      sessionStorage.setItem("error_boundary_recovery", "true");
      
      // Clear the flag after a short delay to allow the app to recover
      setTimeout(() => {
        sessionStorage.removeItem("error_boundary_recovery");
      }, 2000);
      
      // Reset the error state
      this.setState((prevState) => ({
        hasError: false,
        error: undefined,
        errorInfo: undefined,
        retryCount: prevState.retryCount + 1,
      }));
    } else {
      // Max retries reached, reload the page
      window.location.reload();
    }
  };

  // private handleReload = () => { // Unused method removed
  //   window.location.reload();
  // };

  public render() {
    if (this.state.hasError) {
      const canRetry = this.state.retryCount < this.maxRetries;

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 dark:bg-red-900/20 rounded-full">
              <svg
                className="w-6 h-6 text-red-600 dark:text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>

            <h1 className="text-xl font-semibold text-gray-900 dark:text-white text-center mb-2">
              Something went wrong
            </h1>

            <p className="text-gray-600 dark:text-gray-400 text-center mb-6">
              Please contact SelfServe support. We will fix this issue as soon
              as possible.
            </p>

            {import.meta.env.DEV && this.state.error && (
              <details className="mb-6 p-3 bg-red-50 dark:bg-red-900/10 rounded border">
                <summary className="cursor-pointer text-sm font-medium text-red-700 dark:text-red-400 mb-2">
                  Error Details (Development)
                </summary>
                <div className="text-xs font-mono text-red-600 dark:text-red-300 whitespace-pre-wrap break-all">
                  {this.state.error.message}
                  {this.state.error.stack && (
                    <>
                      <br />
                      <br />
                      Stack trace:
                      <br />
                      {this.state.error.stack}
                    </>
                  )}
                </div>
              </details>
            )}

            <div className="flex flex-col space-y-3">
              <button
                onClick={this.handleRetry}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200"
              >
                {canRetry
                  ? `Try Again`
                  : "Reload Page"}
              </button>

              <button
                onClick={() => window.history.back()}
                className="w-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium py-2 px-4 rounded-lg transition-colors duration-200"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
